/* GASSISA
 =========================================================================================
 * Descripción: Generador del recibo de abonos a créditos de clientes.
 *              Conserva un detalle y estado de cuenta estándar; cuando existe
 *              un esquema registrado en Parametros sólo sustituye el encabezado.
 *
 * Historial de cambios:
 * ---------------------------------------------------------------------------------------
 *    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
 * ---------------------------------------------------------------------------------------
 *      1        | 24/10/2026 | José Luis Aragón Cervantes      | Creación
 * ---------------------------------------------------------------------------------------
 */

import { layoutTieneObjetos, resolveTicketLayout } from "./ticketMotorLayout";
import { comandoSeccion, RESET_ESTADO_ESTILOS } from "./ticketEscPos";
import { imprimirTicketEnPrinterTools } from "./ticketImpresion";
import { transliterarTexto } from "./ticketLayoutCatalogs";

const numero = (valor) => Number(valor || 0);
const dinero = (valor) => `$${numero(valor).toFixed(2)}`;
const textoSeguro = (valor) => transliterarTexto(String(valor ?? ""));
const separador = "--------------------------------";

const formatearFecha = (valor) => {
    const texto = String(valor ?? "").trim();
    const coincidencia = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(texto);
    if (!coincidencia) return texto;
    const [, anio, mes, dia, hora = "", minuto = "", segundo = ""] = coincidencia;
    const tiempo = hora ? ` ${hora}:${minuto}${segundo ? `:${segundo}` : ""}` : "";
    return `${dia}/${mes}/${anio}${tiempo}`;
};

const cargarLogoBase = async (parametrosCajaForm) => {
    if (parametrosCajaForm?.logo) return null;
    try {
        const respuesta = await fetch(`${process.env.PUBLIC_URL}/recursos/logocliente.png`);
        return respuesta.ok ? await respuesta.blob() : null;
    } catch {
        return null;
    }
};

export const buildTicketAbonoCreditoParts = ({
    parametrosCajaForm = {},
    abono = {},
    cuenta = {},
    cliente = {},
    totalAbonosPrevios = 0,
    totalAbonado = 0,
    saldoAnterior = 0,
    saldoActual = 0,
}) => {
    const textEncoder = new TextEncoder();
    const partes = [
        textEncoder.encode("\x1B\x40"),
        textEncoder.encode("\x1B\x4B\x60"),
    ];
    const hayEsquema = layoutTieneObjetos(parametrosCajaForm);

    // El esquema personalizado controla únicamente el encabezado. El detalle
    // del abono y el estado de cuenta son siempre legibles y homogéneos.
    if (hayEsquema) {
        const layout = resolveTicketLayout(parametrosCajaForm);
        const encabezado = comandoSeccion(
            textEncoder,
            layout.cabecero,
            parametrosCajaForm.encabezadoticket || ""
        );
        if (!encabezado.multilinea) partes.push(textEncoder.encode("\x1B\x33\x00"));
        partes.push(encabezado.bytes);
        if (encabezado.requiereReset) partes.push(textEncoder.encode(RESET_ESTADO_ESTILOS));
        if (!encabezado.multilinea) partes.push(textEncoder.encode("\x1B\x32"));
    }

    const agregarLinea = (texto, opciones = {}) => {
        const alineacion = opciones.centrado ? "\x1B\x61\x01" : "\x1B\x61\x00";
        const negrita = opciones.negrita ? "\x1B\x45\x01" : "\x1B\x45\x00";
        partes.push(textEncoder.encode(`${alineacion}${negrita}${textoSeguro(texto)}\x1B\x45\x00\n`));
    };

    const folioCredito = abono.foliocredito ?? cuenta.foliocredito ?? "";
    const folioAbono = abono.folioabono ?? "";
    const remision = abono.remision ?? cuenta.remision ?? "";
    const caja = abono.pc ?? cuenta.pc ?? parametrosCajaForm.caja ?? "";
    const nombreCliente = abono.nombrecliente ?? cliente.razonsocial ?? cliente.nombrecliente ?? "";
    const claveCliente = abono.clavecliente ?? cliente.clavecliente ?? cuenta.clavecliente ?? "";
    const montoAbono = numero(abono.montoabono);
    const efectivo = numero(abono.efectivo);
    const formaPago = String(abono.formapago || "EFECTIVO").trim();
    const importeOtroPago = Math.max(montoAbono - efectivo, 0);

    agregarLinea("RECIBO DE ABONO", { centrado: true, negrita: true });
    agregarLinea("CRÉDITO A CLIENTE", { centrado: true, negrita: true });
    agregarLinea(separador);
    agregarLinea(`Folio crédito: ${folioCredito}`, { negrita: true });
    agregarLinea(`Folio abono: ${folioAbono}`);
    agregarLinea(`Folio venta referencia: ${remision}`);
    agregarLinea(`Caja: ${caja}`);
    agregarLinea(`Fecha: ${formatearFecha(abono.fechaabono)}`);
    agregarLinea(separador);
    agregarLinea("DATOS DEL CLIENTE", { centrado: true, negrita: true });
    agregarLinea(`Cliente: ${nombreCliente || "No especificado"}`);
    agregarLinea(`Clave: ${claveCliente}`);
    agregarLinea(separador);
    agregarLinea("DETALLE DEL ABONO", { centrado: true, negrita: true });
    agregarLinea(`Monto aplicado: ${dinero(montoAbono)}`, { negrita: true });
    agregarLinea(`Forma de pago: ${formaPago}`);
    if (efectivo > 0) agregarLinea(`Efectivo: ${dinero(efectivo)}`);
    if (importeOtroPago > 0 && formaPago.toUpperCase() !== "EFECTIVO") {
        agregarLinea(`${formaPago}: ${dinero(importeOtroPago)}`);
    }
    if (abono.referencia) agregarLinea(`Referencia: ${abono.referencia}`);
    if (abono.observacion) agregarLinea(`Observación: ${abono.observacion}`);
    agregarLinea(separador);
    agregarLinea("ESTADO DE CUENTA", { centrado: true, negrita: true });
    agregarLinea(`Importe original del crédito: ${dinero(cuenta.total)}`);
    agregarLinea(`Abonos anteriores: ${dinero(totalAbonosPrevios)}`);
    agregarLinea(`Abono de este recibo: ${dinero(montoAbono)}`, { negrita: true });
    agregarLinea(`Total abonado: ${dinero(totalAbonado)}`);
    agregarLinea(`Saldo anterior: ${dinero(saldoAnterior)}`);
    agregarLinea(`Saldo pendiente: ${dinero(saldoActual)}`, { negrita: true });
    agregarLinea(saldoActual <= 0 ? "ESTADO: SALDADO" : "ESTADO: PENDIENTE", { centrado: true, negrita: true });
    agregarLinea(separador);
    agregarLinea("Conserve este recibo para cualquier aclaración.", { centrado: true });
    partes.push(textEncoder.encode("\x1B\x61\x01\n\n\n\n\x1D\x56\x00"));

    return { partes, hayEsquema };
};

export const imprimirTicketAbonoCredito = async ({
    partes,
    parametrosCajaForm = {},
    nombreImpresora = null,
}) => imprimirTicketEnPrinterTools({
    partes,
    parametrosCajaForm,
    nombreImpresora,
    logoBlob: await cargarLogoBase(parametrosCajaForm),
});
